import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  StaticLandingTodayUpSection,
  StaticLandingTodayUpSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingTodayUpSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingTodayUpMoreFetcherProps} from './StaticLandingTodayUpMoreFetcherProps.interface';

export function StaticLandingTodayUpMoreFetcher({
  children,
  section,
}: StaticLandingTodayUpMoreFetcherProps) {
  const dispatch = useDispatch();
  const staticLandingTodayUpSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingTodayUpSection,
        'groups'
      >
  );
  const {param: currentParam} = staticLandingTodayUpSection;
  const param: StaticLandingTodayUpSectionQueryVariables['param'] = {
    categoryUid: currentParam.categoryUid,
    subcategoryUid: currentParam.subcategory?.param,
  };

  const {data, isSuccess, fetchNextPage, isFetching, isFetchedAfterMount} =
    useInfiniteStaticLandingTodayUpSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {sectionId: section.id, param},
        getNextPageParam: lastPage => {
          const section =
            lastPage.staticLandingTodayUpSection as StaticLandingTodayUpSection;
          return section.isEnd
            ? undefined
            : {
                param: {
                  ...param,
                  page: (section.param.page || 0) + 1,
                },
                sectionId: section.id,
              };
        },
        initialData: staticLandingTodayUpSection
          ? {
              pages: [
                {
                  staticLandingTodayUpSection:
                    staticLandingTodayUpSection as StaticLandingTodayUpSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );
  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingTodayUpSection as StaticLandingTodayUpSection | undefined;
  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
